import { useState } from 'react';
import { observer } from 'mobx-react';
import { routes } from '$src/routes';
import { generatePath } from 'react-router-dom';
import useLocationContext from '$src/hooks/useLocationContext';
import useStores from '$src/hooks/useStores';
import { handleBackToRingDashboard } from '$src/utils/handleBack';
import {
  redirectToOnboardingDenied,
  redirectToOnboardingError,
  redirectToOnboardingSetupComplete,
  redirectToPracticeMode,
} from '$src/utils/redirect';
import useModalEvents from '$src/hooks/useModalEvents';
import AuthorizeDialog from '$src/components/AuthorizeDialog';
import OnboardingContainer from '$src/components/OnboardingContainer';
import aboutImage from '$src/assets/img/onboardingAbout.png';
import { SettingsGearIcon } from '@ring-icons';
import Settings from '$src/components/Settings';
import useRedirectBasedOnMonitoringStatus from './useRedirectBasedOnMonitoringStatus';

import useCustomSettings from './useCustomSettings';

function Introduction() {
  const locationId = useLocationContext();
  const [toggleModal, showModal] = useModalEvents();
  const {
    locationContextStore: { completeOnboarding },
    sessionStore: { isAuthorizationAccepted, authorizeVSG, user, isMobile },
    modesStore: { isPracticeMode },
  } = useStores();
  const [loading, setLoading] = useState(false);

  const onPrimaryActionHandler = async () => {
    if (!isAuthorizationAccepted) {
      toggleModal();
    } else {
      try {
        await completeOnboarding(user.user_id, locationId);
        if (isPracticeMode) redirectToPracticeMode(locationId);
        else redirectToOnboardingSetupComplete(locationId);
      } catch (error) {
        redirectToOnboardingError(locationId);
      }
    }
  };

  const onAuthorize = async () => {
    setLoading(true);
    try {
      const setupComplete = generatePath(`${routes.onboardingSetupComplete}`, { locationId });
      const practiceMode = generatePath(`${routes.onboardingPracticeMode}`, { locationId });
      await completeOnboarding(user.user_id, locationId);
      await authorizeVSG(isPracticeMode ? practiceMode : setupComplete);
    } catch (error) {
      redirectToOnboardingError(locationId);
    }
  };

  useCustomSettings(user.user_id, locationId);
  useRedirectBasedOnMonitoringStatus(user.user_id, locationId);

  const onDenyAuth = () => {
    toggleModal();
    redirectToOnboardingDenied(locationId);
  };

  return (
    <OnboardingContainer
      headerTitle="Virtual Security Guard"
      hideBack
      handleClose={() => handleBackToRingDashboard(isMobile)}
      icon={SettingsGearIcon}
      title="Setup Virtual Security Guard"
      image={aboutImage}
      data-testid="onboarding-introduction"
      primaryText="Complete Setup"
      primaryActionHandler={onPrimaryActionHandler}
      warningOnClose
      warningOnBack
    >
      <Settings
        settingText={
          <>
            <b>Settings</b>
            <p>
              Below are Virtual Security Guard default settings. To continue with the default settings, click "Complete Setup" below. To modify
              settings, click below or update them anytime in the Ring app.
            </p>
          </>
        }
      />
      <AuthorizeDialog
        showModal={showModal}
        setModalVisible={toggleModal}
        handleAuthorize={onAuthorize}
        handleDeny={onDenyAuth}
        loadingButton={loading}
      />
    </OnboardingContainer>
  );
}

export default observer(Introduction);
